import { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useHiveCoreOptional } from '../../services/hivecore-scene-stream/useHiveCoreOptional';

async function safeJsonParse(response: Response): Promise<any> {
  const contentType = response.headers.get('content-type') || '';
  
  if (!contentType.includes('application/json')) {
    const text = await response.text();
    throw new Error(`Non-JSON response (${response.status}): ${text.slice(0, 120)}...`);
  }
  
  try {
    return await response.json();
  } catch (error) {
    const text = await response.text();
    throw new Error(`Failed to parse JSON: ${text.slice(0, 120)}...`);
  }
}

interface AllowlistEntry {
  url: string;
  label: string;
  icon?: string;
}

interface AbdaConfig {
  allowlist: AllowlistEntry[];
  homeUrl: string;
  tenantId: string;
}

interface SessionInfo {
  sessionId: string;
  tenantId: string;
  status: string;
  url?: string;
  createdAt?: string;
  lastUsedAt?: string;
}

interface PolicyCheckResult {
  url: string;
  isAllowed: boolean;
  canEmbed: boolean;
  needsRemoteRender: boolean;
}

interface UseAbdaOptions {
  autoConnect?: boolean;
  tenantId?: string;
  abdaMode?: boolean;
  sendIntent?: (name: string, args?: any) => string | null;
}

interface UseAbdaReturn {
  config: AbdaConfig | null;
  session: SessionInfo | null;
  isLoading: boolean;
  error: string | null;
  currentUrl: string | null;
  isConnected: boolean;
  
  startSession: (options?: { persist?: boolean; storageStatePath?: string }) => Promise<void>;
  stopSession: () => Promise<void>;
  navigateTo: (url: string) => Promise<void>;
  checkPolicy: (url: string) => Promise<PolicyCheckResult>;
  takeScreenshot: () => Promise<Blob>;
  getSnapshot: () => Promise<{ url: string; title: string; content: string }>;
  evaluateScript: (script: string) => Promise<any>;
}

const STORAGE_KEY_SESSION = 'abda:sessionId';
const STORAGE_KEY_URL = 'abda:currentUrl';

export function useAbda(options: UseAbdaOptions = {}): UseAbdaReturn {
  const hc = useHiveCoreOptional();
  const providerMode = !!hc?.sendIntent;
  const providerSendIntent = hc?.sendIntent;
  
  const { autoConnect = false, tenantId = 'default' } = options;
  const abdaMode = options.abdaMode ?? providerMode;
  const sendIntentFn = options.sendIntent ?? providerSendIntent;
  
  const [config, setConfig] = useState<AbdaConfig | null>(null);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  
  const socketRef = useRef<Socket | null>(null);
  const pendingResumeRef = useRef<string | null>(null);
  const attachedSessionRef = useRef<string | null>(null);
  const attachInFlightRef = useRef(false);
  const attachTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const attachAttemptIdRef = useRef(0);

  const getApiBase = useCallback(() => {
    const backend = import.meta.env.VITE_BACKEND_URL;
    const isProd = import.meta.env.PROD && !!backend;
    return isProd ? backend : '';
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const apiBase = getApiBase();
      const response = await fetch(`${apiBase}/api/abda/config`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch ABDA config');
      }
      
      const data = await safeJsonParse(response);
      setConfig({
        allowlist: data.allowlist,
        homeUrl: data.homeUrl,
        tenantId: data.tenantId,
      });
    } catch (err) {
      console.error('Failed to fetch ABDA config:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  }, [getApiBase]);

  const connectSocket = useCallback(() => {
    if (abdaMode) {
      console.log('[ABDA] Skipping legacy WebSocket connection in ABDA 2.0 mode');
      return;
    }

    if (socketRef.current?.connected) {
      return;
    }

    const backend = import.meta.env.VITE_BACKEND_URL;
    const isProd = import.meta.env.PROD && !!backend;
    
    const socket = io(isProd ? `${backend}/abda` : '/abda', {
      path: '/socket.io',
      auth: { tenantId },
      query: { tenantId },
      transports: isProd ? ['websocket'] : ['websocket', 'polling'],
    });

    const clearAttachState = () => {
      attachInFlightRef.current = false;
      if (attachTimeoutRef.current) {
        clearTimeout(attachTimeoutRef.current);
        attachTimeoutRef.current = null;
      }
    };

    const maybeAttach = () => {
      const sessionId = pendingResumeRef.current;
      if (!sessionId || attachInFlightRef.current || !socket.connected) {
        return;
      }
      
      attachInFlightRef.current = true;
      const attemptId = ++attachAttemptIdRef.current;
      console.log('[ABDA] Emitting attach event for session:', sessionId, 'attempt:', attemptId);
      
      attachTimeoutRef.current = setTimeout(() => {
        if (attachAttemptIdRef.current !== attemptId) return;
        console.warn('[ABDA] Attach timeout for attempt:', attemptId);
        clearAttachState();
        if (socket.connected && pendingResumeRef.current) {
          maybeAttach();
        }
      }, 10000);
      
      socket.emit('attach', { sessionId }, (ack?: any) => {
        if (attachAttemptIdRef.current !== attemptId) return;
        
        if (ack?.success) {
          clearAttachState();
          attachedSessionRef.current = sessionId;
          pendingResumeRef.current = null;
          console.log('[ABDA] Successfully attached to session:', sessionId, 'attempt:', attemptId);
        } else if (ack?.code === 'INVALID_SESSION') {
          clearAttachState();
          pendingResumeRef.current = null;
          localStorage.removeItem(STORAGE_KEY_SESSION);
          localStorage.removeItem(STORAGE_KEY_URL);
          console.warn('[ABDA] Attach failed - invalid session:', attemptId);
        } else {
          clearAttachState();
          console.warn('[ABDA] Attach response ambiguous:', ack, 'attempt:', attemptId);
        }
      });
    };

    socket.on('connect', () => {
      console.log('[ABDA] Socket connected');
      setIsConnected(true);
      maybeAttach();
    });

    socket.on('connect_error', (err) => {
      console.error('[ABDA] connect_error:', err.message, err);
      setIsConnected(false);
    });

    socket.on('reconnect_error', (err) => {
      console.error('[ABDA] reconnect_error:', err.message, err);
      setIsConnected(false);
    });

    socket.on('disconnect', () => {
      console.log('[ABDA] Socket disconnected');
      clearAttachState();
      setIsConnected(false);
      setSession(null);
      if (attachedSessionRef.current) {
        pendingResumeRef.current = attachedSessionRef.current;
      }
      attachedSessionRef.current = null;
    });

    socket.on('status', (data: any) => {
      console.log('[ABDA] Status:', data);
      if (data.status === 'started' && data.sessionId) {
        const sessionInfo = {
          sessionId: data.sessionId,
          tenantId: data.tenantId,
          status: data.status,
          url: data.url,
        };
        setSession(sessionInfo);
        localStorage.setItem(STORAGE_KEY_SESSION, data.sessionId);
        if (data.url) {
          setCurrentUrl(data.url);
          localStorage.setItem(STORAGE_KEY_URL, data.url);
        }
      } else if (data.status === 'attached' && data.sessionId) {
        clearAttachState();
        attachedSessionRef.current = data.sessionId;
        pendingResumeRef.current = null;
        const sessionInfo = {
          sessionId: data.sessionId,
          tenantId: data.tenantId,
          status: 'active',
          url: data.url,
        };
        setSession(sessionInfo);
        localStorage.setItem(STORAGE_KEY_SESSION, data.sessionId);
        if (data.url) {
          setCurrentUrl(data.url);
          localStorage.setItem(STORAGE_KEY_URL, data.url);
        }
      } else if (data.status === 'stopped') {
        clearAttachState();
        pendingResumeRef.current = null;
        attachedSessionRef.current = null;
        setSession(null);
        setCurrentUrl(null);
        localStorage.removeItem(STORAGE_KEY_SESSION);
        localStorage.removeItem(STORAGE_KEY_URL);
      }
    });

    socket.on('config:update', (data: any) => {
      console.log('[ABDA] Config updated:', data);
      fetchConfig();
    });

    socket.on('navigated', (data: any) => {
      console.log('[ABDA] Navigated:', data);
      setCurrentUrl(data.url);
    });

    socket.on('error', (data: any) => {
      console.error('[ABDA] Error:', data);
      clearAttachState();
      setError(data.message || 'Unknown error');
    });

    socket.on('connect_error', (error: Error) => {
      console.error('[ABDA] Connect error:', error);
      clearAttachState();
    });

    socket.on('reconnect_error', (error: Error) => {
      console.error('[ABDA] Reconnect error:', error);
      clearAttachState();
    });

    socket.on('reconnect_failed', () => {
      console.error('[ABDA] Reconnect failed');
      clearAttachState();
    });

    socket.io.on('reconnect', () => {
      console.log('[ABDA] Socket reconnected');
      maybeAttach();
    });

    socketRef.current = socket;
    
    if (socket.connected) {
      maybeAttach();
    }
  }, [tenantId]);

  const disconnectSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    }
  }, []);

  const resumeSession = useCallback(async (sessionId: string) => {
    if (abdaMode) {
      console.log('[ABDA] Skipping legacy session resume in ABDA 2.0 mode');
      return false;
    }

    try {
      const apiBase = getApiBase();
      const response = await fetch(`${apiBase}/api/abda/attach`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });

      if (!response.ok) {
        localStorage.removeItem(STORAGE_KEY_SESSION);
        localStorage.removeItem(STORAGE_KEY_URL);
        pendingResumeRef.current = null;
        return false;
      }

      const data = await safeJsonParse(response);
      setSession({
        sessionId: data.sessionId,
        tenantId: data.tenantId,
        status: data.status,
        url: data.url,
      });
      
      if (data.url) {
        setCurrentUrl(data.url);
      }

      pendingResumeRef.current = sessionId;

      console.log('[ABDA] Resumed session (HTTP):', sessionId);
      return true;
    } catch (err) {
      console.error('Failed to resume session:', err);
      localStorage.removeItem(STORAGE_KEY_SESSION);
      localStorage.removeItem(STORAGE_KEY_URL);
      pendingResumeRef.current = null;
      return false;
    }
  }, [getApiBase]);

  const startSession = useCallback(async (options: { persist?: boolean; storageStatePath?: string } = {}) => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (abdaMode && sendIntentFn) {
        console.log('[ABDA] Starting session via scene intent (ABDA 2.0 mode)');
        const cid = sendIntentFn('abda.session.start', options);
        if (cid) {
          const sessionId = `session_${Date.now()}`;
          const sessionInfo = {
            sessionId,
            tenantId,
            status: 'starting',
          };
          setSession(sessionInfo);
          localStorage.setItem(STORAGE_KEY_SESSION, sessionId);
          console.log('[ABDA] Session start intent sent:', cid);
        } else {
          throw new Error('Failed to send session start intent');
        }
        return;
      }

      if (!socketRef.current?.connected) {
        connectSocket();
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      const apiBase = getApiBase();
      const response = await fetch(`${apiBase}/api/abda/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(options),
      });

      if (!response.ok) {
        throw new Error('Failed to start session');
      }

      const data = await safeJsonParse(response);
      const sessionInfo = {
        sessionId: data.sessionId,
        tenantId: data.tenantId,
        status: data.status,
      };
      setSession(sessionInfo);
      localStorage.setItem(STORAGE_KEY_SESSION, data.sessionId);
    } catch (err) {
      console.error('Failed to start session:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [connectSocket, getApiBase, abdaMode, sendIntentFn, tenantId]);

  const stopSession = useCallback(async () => {
    if (!session?.sessionId) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (abdaMode && sendIntentFn) {
        console.log('[ABDA] Stopping session via scene intent (ABDA 2.0 mode)');
        const cid = sendIntentFn('abda.session.stop', { sessionId: session.sessionId });
        if (cid) {
          setSession(null);
          setCurrentUrl(null);
          localStorage.removeItem(STORAGE_KEY_SESSION);
          localStorage.removeItem(STORAGE_KEY_URL);
          console.log('[ABDA] Session stop intent sent:', cid);
        } else {
          throw new Error('Failed to send session stop intent');
        }
        return;
      }

      const apiBase = getApiBase();
      const response = await fetch(`${apiBase}/api/abda/stop`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-ABDA-Session-ID': session.sessionId,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to stop session');
      }

      setSession(null);
      setCurrentUrl(null);
      localStorage.removeItem(STORAGE_KEY_SESSION);
      localStorage.removeItem(STORAGE_KEY_URL);
    } catch (err) {
      console.error('Failed to stop session:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [session, getApiBase, abdaMode, sendIntentFn]);

  const navigateTo = useCallback(async (url: string) => {
    if (!session?.sessionId) {
      throw new Error('No active session');
    }

    setIsLoading(true);
    setError(null);

    try {
      if (abdaMode && sendIntentFn) {
        console.log('[ABDA] Navigating via scene intent (ABDA 2.0 mode):', url);
        const cid = sendIntentFn('abda.navigate', { sessionId: session.sessionId, url });
        if (cid) {
          setCurrentUrl(url);
          localStorage.setItem(STORAGE_KEY_URL, url);
          console.log('[ABDA] Navigate intent sent:', cid);
        } else {
          throw new Error('Failed to send navigate intent');
        }
        return;
      }

      const apiBase = getApiBase();
      const response = await fetch(`${apiBase}/api/abda/goto`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-ABDA-Session-ID': session.sessionId,
        },
        body: JSON.stringify({ url }),
      });

      if (!response.ok) {
        const errorData = await safeJsonParse(response);
        throw new Error(errorData.error || 'Failed to navigate');
      }

      const data = await safeJsonParse(response);
      setCurrentUrl(data.url);
      localStorage.setItem(STORAGE_KEY_URL, data.url);
    } catch (err) {
      console.error('Failed to navigate:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [session, getApiBase, abdaMode, sendIntentFn]);

  const checkPolicy = useCallback(async (url: string): Promise<PolicyCheckResult> => {
    try {
      const apiBase = getApiBase();
      const response = await fetch(`${apiBase}/api/abda/policy/check?url=${encodeURIComponent(url)}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to check policy');
      }

      const data = await safeJsonParse(response);
      return {
        url: data.url,
        isAllowed: data.isAllowed,
        canEmbed: data.canEmbed,
        needsRemoteRender: data.needsRemoteRender,
      };
    } catch (err) {
      console.error('Failed to check policy:', err);
      throw err;
    }
  }, [getApiBase]);

  const takeScreenshot = useCallback(async (): Promise<Blob> => {
    if (!session?.sessionId) {
      throw new Error('No active session');
    }

    try {
      const apiBase = getApiBase();
      const response = await fetch(`${apiBase}/api/abda/screenshot`, {
        headers: {
          'X-ABDA-Session-ID': session.sessionId,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to take screenshot');
      }

      return await response.blob();
    } catch (err) {
      console.error('Failed to take screenshot:', err);
      throw err;
    }
  }, [session, getApiBase]);

  const getSnapshot = useCallback(async (): Promise<{ url: string; title: string; content: string }> => {
    if (!session?.sessionId) {
      throw new Error('No active session');
    }

    try {
      const apiBase = getApiBase();
      const response = await fetch(`${apiBase}/api/abda/snapshot`, {
        headers: {
          'X-ABDA-Session-ID': session.sessionId,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get snapshot');
      }

      const data = await safeJsonParse(response);
      return data.snapshot;
    } catch (err) {
      console.error('Failed to get snapshot:', err);
      throw err;
    }
  }, [session, getApiBase]);

  const evaluateScript = useCallback(async (script: string): Promise<any> => {
    if (!session?.sessionId) {
      throw new Error('No active session');
    }

    try {
      const apiBase = getApiBase();
      const response = await fetch(`${apiBase}/api/abda/eval`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-ABDA-Session-ID': session.sessionId,
        },
        body: JSON.stringify({ script }),
      });

      if (!response.ok) {
        throw new Error('Failed to evaluate script');
      }

      const data = await safeJsonParse(response);
      return data.result;
    } catch (err) {
      console.error('Failed to evaluate script:', err);
      throw err;
    }
  }, [session, getApiBase]);

  useEffect(() => {
    fetchConfig();
    
    const savedSessionId = localStorage.getItem(STORAGE_KEY_SESSION);
    const savedUrl = localStorage.getItem(STORAGE_KEY_URL);
    
    if (savedSessionId) {
      console.log('[ABDA] Found saved session, attempting to resume:', savedSessionId);
      resumeSession(savedSessionId).then(success => {
        if (success && savedUrl) {
          setCurrentUrl(savedUrl);
        }
      });
    }
    
    if (autoConnect) {
      connectSocket();
    }

    return () => {
      disconnectSocket();
    };
  }, [fetchConfig, autoConnect, connectSocket, disconnectSocket, resumeSession]);

  return {
    config,
    session,
    isLoading,
    error,
    currentUrl,
    isConnected,
    startSession,
    stopSession,
    navigateTo,
    checkPolicy,
    takeScreenshot,
    getSnapshot,
    evaluateScript,
  };
}
